// Angular
import { Injectable } from '@angular/core';

// Rxjs
import { BehaviorSubject, Observable, throwError } from 'rxjs';

// Moment
import * as moment from 'moment';

// Services
import { ApiService } from '../api/api.service';

// Helpers
import { MAX_VALUE_DATE, MIN_VALUE_DATE, isUndefined } from './../../lib/helpers/utils';

// Models
import { Address, AddressHelper } from '../../models/b2b/address';
import { Bill } from './../../models/b2b/bill';
import { ClientHelper, Client } from '../../models/b2b/client';
import { Delivery, DeliveryHelper } from './../../models/b2b/delivery';
import { DeliveryStatusHelper, DeliveryStatus } from '../../models/b2b/deliveryStatus';
import { Expiration, ExpirationHelper } from './../../models/b2b/expiration';
import { Note, NoteHelper } from './../../models/b2b/note';
import { User, UserType, UserHelper } from './../../models/b2b/user';
import { AgencieStore, AgencieStoreHelper } from '../../models/b2b/agencieStore';
import { SearchSettings } from '../../models/b2b/searchSettings';
import { Store } from '@ngrx/store';
import { AppState } from '../../../store';
import { Subscriber } from '../../lib/helpers/subscriber';
import { Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { of } from 'rxjs';
import { validateApiResponse } from 'app/shared/lib/custom-rxjs-operators/api-response.operator';

@Injectable()
export class SoledadUserService extends Subscriber {
    // Cache of prints
    private cachePrint = {};

    private addressCode: number = null;

    addressManualChangeSubject = new Subject<number>();
    clientChangeInProgressSubject = new Subject<boolean>();

    private almacenesAgenciasSubject = new BehaviorSubject<AgencieStore[]>([]);
    private currencySubject = new BehaviorSubject<string>('EUR');

    empresaMarroqui = 49;

    constructor(
        private api: ApiService,
        private store: Store<AppState>
    ) {
        super();
        this.subscribeSelectedAddress();
        this.subscribeUser();
        this.subscribeEmpresa();
    }

    get almacenesAgencias$(): Observable<AgencieStore[]> {
        return this.almacenesAgenciasSubject.asObservable();
    }

    get currency$(): Observable<string> {
        return this.currencySubject.asObservable();
    }

    subscribeSelectedAddress(): void {
        this.addSubscription(
            this.store
                .select((state) => state.user.data.selectedAddressCode)
                .subscribe((selectedAddress) => {
                    this.addressCode = selectedAddress;
                })
        );
    }

    subscribeUser(): void {
        this.addSubscription(
            this.store
                .select((state) => state.user.data)
                .subscribe((user) => {
                    if (user && user.addresses && user.addresses.length) {
                        this.getAgencieStores(user).subscribe((data) => {
                            this.almacenesAgenciasSubject.next(data);
                        });
                    }
                })
        );
    }

    subscribeEmpresa(): void {
        this.addSubscription(
            this.store
                .select((state) => state.user.data.empresa)
                .subscribe((empresa) => {
                    let currency = 'EUR';
                    if (empresa === this.empresaMarroqui) {
                        currency = 'MAD';
                    }
                    this.currencySubject.next(currency);
                })
        );
    }

    /**
     * Obtains data from user
     * @param {string=null} user_id User id
     * @return void
     */
    getData(user_id: string = null): Observable<User> {
        const params = [];
        if (user_id !== null) {
            params['CLIENTE'] = user_id;
        }

        return this.api.callSoledad('b2bNew.dbo.HTML_ClientesFindById', params).pipe(
            validateApiResponse('Error obteniendo datos del usuario' + '. Params: ' + JSON.stringify(params)),
            map((res: any) => {
                return UserHelper.transformFromApi(res[0], this.getAuthTokenExtrasFromStorage(), this.api.environment);
            })
        );
    }

    /**
     * Loads users addresses
     *
     * @param {string} [user_id=null]
     *
     * @memberOf SoledadUserService
     */
    getUserAddresses(user: User = null): Observable<Address[]> {
        // Params
        const params = [];
        // console.log({user});
        if (user !== null) {
            params['CLIENTE'] = user.clienteCodigo;
            return this.api.callSoledad('b2bNew.dbo.HTML_ClientesDirEnvio', params).pipe(
                validateApiResponse('Error obteniendo direcciones de envío' + '. Params: ' + JSON.stringify(params)),
                map((res: any) => {
                    return res.map((row: any) => AddressHelper.transformFromAPI(row));
                })
            );
        }
    }

    getDeliveries(
        user: User,
        search: number = 0,
        status: number = 0,
        from: Date = null,
        to: Date = null,
        measures: number = 0,
        brand: string = ''
    ): Observable<Delivery[]> {
        const params = [];

        // console.log('parametros recibidos ', params);

        params['PEDIDO'] = search === 0 ? '' : search;
        // console.log({search});
        // params['MARCA'] = '';
        params['ESTADOS'] = status;
        params['FECHADESDE'] = from !== null ? moment(from).format('YYYYMMDD') : MIN_VALUE_DATE;
        params['FECHAHASTA'] = to !== null ? moment(to).format('YYYYMMDD') : MAX_VALUE_DATE;
        params['BUSQUEDA'] = measures === 0 ? '' : measures;
        params['MARCA'] = brand;

        if (user != null) {
            if (user.type === UserType.Comercial) {
                params['DIRECCION'] = this.addressCode;
                params['CLIENTE'] = user.clienteCodigo;
            }

            return this.api.callSoledad('b2bNew.dbo.HTML_PedidosBusqueda', params).pipe(
                validateApiResponse('Error obteniendo pedidos' + '. Params: ' + JSON.stringify(params)),
                map((res: any) => {
                    return res.map((row: any) => DeliveryHelper.transformFromAPI(row));
                })
            );
        }
    }

    getDeliveryStatus(settings: SearchSettings): Observable<DeliveryStatus[]> {
        const params = [];

        params['IDIOMA'] = settings ? settings.language : 'es';
        // params['IDIOMA'] = this.translate.currentLang;

        // console.log('getDeliveryStatus', settings.language);

        return this.api.callSoledad('b2bNew.dbo.HTML_PedidosEstados', params).pipe(
            validateApiResponse('Error obteniendo estados de pedidos' + '. Params: ' + JSON.stringify(params)),
            map((res: any) => {
                return res.map((row: any) => DeliveryStatusHelper.transformFromAPI(row));
            })
        );
    }

    getBills(search: string, user: User, from: Date = null, to: Date = null): Observable<Bill[]> {
        const params = [];
        // params['FACTURA'] = 999999;
        // params['NUMERO'] = 999999;
        // params['ID'] = 999999;
        params['FECHADESDE'] = from !== null ? moment(from).format('YYYYMMDD') : MIN_VALUE_DATE;
        params['FECHAHASTA'] = to !== null ? moment(to).format('YYYYMMDD') : MAX_VALUE_DATE;

        // console.log({search})

        if (user === null) {
            return of([]);
        }
        if (user.type === UserType.Comercial) {
            params['CLIENTE'] = user.clienteCodigo;
            params['DIRECCION'] = this.addressCode;
        }

        if (search) {
            params['FACTURAS'] = search;
        }

        return this.api.callSoledad('b2bNew.dbo.HTML_FacturasBusqueda', params).pipe(
            validateApiResponse('Error obteniendo facturas' + '. Params: ' + JSON.stringify(params)),
            map((res: any) => {
                return res.map((row: any) => Bill.transformFromApi(row));
            })
        );
    }

    deliveryPrint(serie, pedido, fecha: Date): Observable<string> {
        const params = [];

        // params.CLIENTE = user.data.CL_CODIGO; //??
        params['SERIE'] = serie;
        params['PEDIDO'] = pedido;
        params['ANIO'] = moment(fecha).format('YYYY');

        const id_cache = 'p' + params['SERIE'] + params['PEDIDO'] + params['ANIO'];

        if (id_cache in this.cachePrint) {
            return of(this.cachePrint[id_cache]);
        }

        return this.api.callSoledad('b2bNew.dbo.HTML_PedidosImprimir', params).pipe(
            validateApiResponse('Error obteniendo impresión de pedido' + '. Params: ' + JSON.stringify(params)),
            map((res: any) => {
                this.cachePrint[id_cache] = res[0]['IM_URL'];
                return this.cachePrint[id_cache];
            })
        );
    }

    notePrint(serie, albaran, fecha: Date): Observable<string> {
        const params = [];

        // params.CLIENTE = user.data.CL_CODIGO; //??
        params['SERIE'] = serie;
        params['ALBARAN'] = albaran;
        params['ANIO'] = moment(fecha).format('YYYY');

        const id_cache = 'a' + params['SERIE'] + params['ALBARAN'] + params['ANIO'];

        if (id_cache in this.cachePrint) {
            return of(this.cachePrint[id_cache]);
        }

        return this.api.callSoledad('b2bNew.dbo.HTML_AlbaranesImprimir', params).pipe(
            validateApiResponse('Error obteniendo impresión de albarán' + '. Params: ' + JSON.stringify(params)),
            map((res: any) => {
                this.cachePrint[id_cache] = res[0]['IM_URL'];
                return this.cachePrint[id_cache];
            })
        );
    }

    billPrint(serie, factura, fecha: Date): Observable<string> {
        const params = [];

        // params.CLIENTE = user.data.CL_CODIGO; //??
        params['SERIE'] = serie;
        params['FACTURA'] = factura;
        params['ANIO'] = moment(fecha).format('YYYY');

        const id_cache = 'F' + params['SERIE'] + params['FACTURA'] + params['ANIO'];

        if (id_cache in this.cachePrint) {
            return of(this.cachePrint[id_cache]);
        }

        return this.api.callSoledad('b2bNew.dbo.HTML_FacturasImprimir', params).pipe(
            validateApiResponse('Error obteniendo impresión de factura' + '. Params: ' + JSON.stringify(params)),
            map((res: any) => {
                // console.log({res});
                this.cachePrint[id_cache] = res[0]['IM_URL'];
                return this.cachePrint[id_cache];
            })
        );
    }

    getNotes(search: string, user: User, from: Date = null, to: Date = null, address: number = -1): Observable<Note[]> {
        const params = [];

        params['FECHADESDE'] = from !== null ? moment(from).format('YYYYMMDD') : MIN_VALUE_DATE;
        params['FECHAHASTA'] = to !== null ? moment(to).format('YYYYMMDD') : MAX_VALUE_DATE;

        if (address >= 0) {
            params['DIRECCION'] = address;
        }

        if (search) {
            params['ALBARANES'] = search;
        }

        if (!user) {
            return of([]);
        }

        if (user.type === UserType.Comercial) {
            params['CLIENTE'] = user.clienteCodigo;
        }

        return this.api.callSoledad('b2bNew.dbo.HTML_AlbaranesBusqueda', params).pipe(
            validateApiResponse('Error obteniendo albaranes' + '. Params: ' + JSON.stringify(params)),
            map((res: any) => {
                return res.map((row: any) => NoteHelper.transformFromApi(row));
            })
        );
    }

    getExpirations(search: string, user: User, from: Date = null, to: Date = null): Observable<Expiration[]> {
        const params = [];

        params['FECHADESDE'] = from !== null ? moment(from).format('YYYYMMDD') : MIN_VALUE_DATE;
        params['FECHAHASTA'] = to !== null ? moment(to).format('YYYYMMDD') : MAX_VALUE_DATE;

        if (!user) {
            return of([]);
        }

        if (search) {
            params['DOCUMENTO'] = search;
        }

        if (user.type === UserType.Comercial) {
            params['CLIENTE'] = user.clienteCodigo;
        }

        return this.api.callSoledad('b2bNew.dbo.HTML_VencimientosBusqueda', params).pipe(
            validateApiResponse('Error obteniendo vencimientos' + '. Params: ' + JSON.stringify(params)),
            map((res: any) => {
                return res.map((row: any) => ExpirationHelper.transformFromApi(row));
            })
        );
    }

    getClients(busqueda: string = ''): Observable<Client[]> {
        const params = [];
        params['BUSQUEDA'] = busqueda;

        return this.api.callSoledad('b2bNew.dbo.HTML_ClientesBusqueda', params).pipe(
            validateApiResponse('Error obteniendo clientes' + '. Params: ' + JSON.stringify(params)),
            map((res: any) => {
                return res.map((row: any) => ClientHelper.transformFromAPI(row));
            })
        );
    }

    getUserMd5(): string {
        if (sessionStorage.getItem('userMd5')) {
            // console.log(sessionStorage.getItem('userMd5'));
            return sessionStorage.getItem('userMd5');
        }
    }

    getAuthTokenExtrasFromStorage(): string {
        if (sessionStorage.getItem('authTokenExtras')) {
            return sessionStorage.getItem('authTokenExtras');
        }

        if (localStorage.getItem('authTokenExtras')) {
            return localStorage.getItem('authTokenExtras');
        }
    }

    getUrlRedirectFromStorage(): string {
        if (sessionStorage.getItem('urlRedirectToken')) {
            return sessionStorage.getItem('urlRedirectToken');
        }

        if (localStorage.getItem('urlRedirectToken')) {
            return localStorage.getItem('urlRedirectToken');
        }
    }

    getAgencieStores(user: User): Observable<AgencieStore[]> {
        const params = [];

        if (user) {
            const defaultAddress = UserHelper.getUserDefaultAddress(user);
            // console.log({ defaultAddress });

            if (defaultAddress) {
                params['CLIENTE'] = user.clienteCodigo;
                params['DIRECCION'] =
                    defaultAddress.codigo || typeof defaultAddress.codigo === 'number' ? defaultAddress.codigo : null;
                // params['DIRECCION'] = 1;
                params['ALMACEN'] = defaultAddress.almacenCodigo || null;
                // params['ALMACEN'] = 33;
                params['AGENCIA'] = defaultAddress.agencia || null;
            }

            // console.log({params});

            return this.api.callSoledad('b2bNew.dbo.HTML_AlmacenesAgenciasHoras', params).pipe(
                validateApiResponse('Error obteniendo almacenes y agencias' + '. Params: ' + JSON.stringify(params)),
                map((res: any) => {
                    return res.map((row: any) => AgencieStoreHelper.transformFromAPI(row));
                })
            );
        }
    }

    changeVersion(user: User): Observable<string> {
        if (!user) {
            return;
        }

        const params = [];
        params['VERSION'] = user.versionb2b === 'current' ? 'new' : 'current';

        return this.api.callSoledad('b2bNew.dbo.HTML_UsuariosCambioVersion', params).pipe(
            validateApiResponse('Error cambiando versión' + '. Params: ' + JSON.stringify(params)),
            map((res: any) => {
                // console.log('HTML_UsuariosCambioVersion', {res})
                return params['VERSION'];
            })
        );
    }

    getReplacementOrders(filter: any): Observable<any[]> {
        const params = [];

        params['CLIENTE'] = filter.user.clienteCodigo;

        if (!isUndefined(filter.address)) {
            params['DIRECCION_ENVIO'] = filter.address;
        }

        return this.api.callSoledad('b2bNew.dbo.HTML_ReposicionBuscar', params).pipe(
            validateApiResponse('Error obteniendo pedidos de reposición' + '. Params: ' + JSON.stringify(params)),
            map((res: any) => {
                return res.map((row: any) => row);
            })
        );
    }

    deleteReplacementOrder(id: string): Observable<any> {
        const params = [];
        const ret = {
            result: false,
            message: ''
        };

        params['ID'] = id;

        return this.api.callSoledad('b2bNew.dbo.HTML_ReposicionEliminar', params).pipe(
            validateApiResponse('Error eliminando pedido de reposición' + '. Params: ' + JSON.stringify(params)),
            map(
                (res) => {
                    ret.result = true;
                    return ret;
                },
                (error) => console.log({ error })
            )
        );
    }

    getIdQtyProductsFromReplacementOrder(id: string): Observable<any> {
        const ids = [];
        const params = [];
        const mapProducts: {
            [index: string]: number;
        } = {};

        params['ID'] = id;

        return this.api.callSoledad('b2bNew.dbo.HTML_ReposicionLeer', params).pipe(
            map(
                (res) => {
                    // console.log('[UserService] getIdQtyProductsFromReplacementOrder', res);

                    if (res.result === 0) {
                        res.value.map((item) => {
                            mapProducts[item.ARTICULO_CODIGO] = item.CANTIDAD;
                            ids.push(item.ARTICULO_CODIGO);
                        });

                        return { mapProducts, ids };
                    }
                },
                (error) => {}
            )
        );
    }
}
